import React, { useState, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import { format } from "date-fns";
import cls from "classnames";

import { ReactComponent as Search } from "../search.svg";
import { ReactComponent as Delete } from "../delete.svg";
import { ReactComponent as Close } from "../close.svg";

const CATEGORY = "CATEGORY";
const DATE = "DATE";
const SEARCH = "SEARCH";
const INITIAL_RANGE = [
  {
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
    initial: true,
  },
];

const FilterSearchBar = ({ categories = [], setSelectedCategory, setPickedDate, handleSearch, className = "" }) => {
  const [hovering, setHovering] = useState();
  const [selected, setSelected] = useState();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [ranges, setRanges] = useState(INITIAL_RANGE);
  const [search, setSearch] = useState("");
  const elementRef = useRef();
  const mql = window.matchMedia("(max-width: 640px)");

  useEffect(() => {
    document.addEventListener("click", (e) => {
      if (e.target.id === SEARCH || e.target.id === "delete") return;
      if (elementRef.current && !elementRef.current.contains(e.target)) {
        setSelected(null);
      }
    });

    return () => {};
  }, []);

  function onClick(e) {
    switch (e.currentTarget.id) {
      case CATEGORY:
        setSelected(CATEGORY);
        break;
      case DATE:
        setSelected(DATE);
        break;
      case SEARCH:
        setSelected(SEARCH);
        break;
      default:
        break;
    }
  }

  function onDateRangeChange(range) {
    range.selection.initial = false;
    setRanges([range.selection]);
  }

  function reset() {
    setRanges(INITIAL_RANGE);
    setSelectedCategories([]);
    setSelectedCategory?.(null);
    setPickedDate(null);
    setSelected(null);
    setSearch("");
    handleSearch({ target: { value: "" } });
  }

  function confirm() {
    if (selected === CATEGORY) setSelectedCategory?.(selectedCategories);
    if (selected === DATE && !ranges[0].initial) setPickedDate?.([ranges[0].startDate, ranges[0].endDate]);
    setSelected(null);
  }

  return (
    <div
      id="filter-searchbar-container"
      ref={elementRef}
      className={cls(`tw:relative ${className}`, {
        "tw:bg-accent": selected === CATEGORY || selected === DATE,
      })}
    >
      <div
        id="filter-searchbar"
        className={`tw:flex tw:items-center tw:justify-between tw:border-b-1 tw:border-tw-secondary tw:h-[60px] tw:px-[10px]`}
      >
        <div
          className={cls("tw:flex tw:py-[10px]", {
            "tw:hidden": selected === SEARCH,
          })}
        >
          <span
            id={CATEGORY}
            className={cls(`tab`, {
              "tw:opacity-30": hovering && hovering !== CATEGORY,
              "tw:text-white": selected === CATEGORY,
              "tw:text-tw-secondary": selected && selected !== CATEGORY,
            })}
            onClick={onClick}
            onMouseOver={(e) => setHovering(e.currentTarget.id)}
            onMouseOut={() => setHovering(null)}
          >
            CATEGORY
          </span>
          <span className="tw:border-r-1 tw:mx-[10px] tw:border-tw-secondary tw:sm:mx-[20px]" />
          <span
            id={DATE}
            className={cls(`tab`, {
              "tw:opacity-30": hovering && hovering !== DATE,
              "tw:text-white": selected === DATE,
              "tw:text-tw-secondary": selected && selected !== DATE,
            })}
            onClick={onClick}
            onMouseOver={(e) => setHovering(e.currentTarget.id)}
            onMouseOut={() => setHovering(null)}
          >
            DATE
          </span>
        </div>

        <div
          className={cls("tw:flex tw:py-[10px] tw:translate-x-[8px] tw:items-center", {
            "tw:flex-1": selected === SEARCH,
          })}
        >
          <p
            className={cls(`tw:m-0 tw:leading-[30px] tw:font-flecha tw:text-[18px] tw:sm:text-[25px]`, {
              "tw:text-tw-secondary": ranges[0].initial || !selected,
              "tw:text-white": !ranges[0].initial && selected,
              "tw:hidden": selected === SEARCH,
            })}
          >
            {ranges[0].initial
              ? "00/00/0000 — 00/00/0000"
              : `${format(ranges[0].startDate, "MM/dd/yyyy")} - ${format(ranges[0].endDate, "MM/dd/yyyy")}`}
          </p>
          <div
            className={cls("tw:flex tw:flex-1 tw:justify-between", {
              "tw:hidden": selected !== SEARCH,
            })}
          >
            <input
              className="tw:flex-1 tw:font-flecha tw:text-[18px] tw:sm:text-[25px]"
              type="text"
              value={search}
              placeholder="ENTER SEARCH"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch({ target: { value: search } });
                }
              }}
            />
            <button
              className="tw:bg-tw-primary btn-tw-primary tw:text-white"
              onClick={() => {
                if (search.trim() === "") return setSearch("");
                handleSearch({ target: { value: search } });
              }}
            >
              Search
            </button>
          </div>
          <span
            className={cls(
              "tw:border-r-1 tw:ml-[10px] tw:h-[23.25px] tw:sm:h-[37.5px] tw:sm:ml-[20px] tw:sm:mr-[5px]",
              {
                "tw:border-tw-secondary": (hovering && hovering !== SEARCH) || (selected && selected !== SEARCH),
              },
            )}
          />
          {selected !== SEARCH ? (
            <Search
              id={SEARCH}
              className={cls(`tw:cursor-pointer tw:scale-80 tw:translate-y-[-2px]`, {
                "tw:opacity-30": hovering && hovering !== SEARCH,
                "tw:text-white": selected === DATE,
                "tw:text-tw-secondary": selected && selected !== SEARCH,
              })}
              onClick={onClick}
              onMouseOver={(e) => setHovering(e.currentTarget.id)}
              onMouseOut={() => setHovering(null)}
            />
          ) : (
            <Close
              className={`tw:cursor-pointer tw:scale-50`}
              height={32}
              width={31}
              onClick={() => {
                setSelected(null);
                setSearch("");
                handleSearch({ target: { value: "" } });
              }}
            />
          )}
        </div>
      </div>

      <div
        id="flyout"
        className={cls("tw:absolute tw:z-[999] tw:left-0 tw:right-0 tw:bg-accent", {
          "tw:pb-[30px] tw:sm:pb-0": selected && selected !== SEARCH,
        })}
      >
        {selected === CATEGORY ? (
          <div className="tw:py-5 tw:px-5">
            <ul className="tw:p-0 tw:inline-flex tw:flex-col tw:flex-wrap tw:max-h-full tw:sm:max-h-[300px] tw:font-alethia">
              {categories?.map((category) => (
                <li
                  key={category.value}
                  className={`tw:mb-[15px] tw:mr-[40px] tw:cursor-pointer tw:flex tw:items-center ${
                    selectedCategories?.some((cat) => cat.value === category.value) ? "tw:text-gold" : "tw:text-white"
                  }`}
                  onClick={() => {
                    if (selectedCategories?.some((cat) => cat.value === category.value)) {
                      return;
                    }
                    setSelectedCategories([...selectedCategories, category]);
                  }}
                >
                  {category.label}
                  {selectedCategories?.some((cat) => cat.value === category.value) && (
                    <Delete
                      id="delete"
                      height={15}
                      className="tw:inline-block tw:ml-[5px] tw:**:pointer-events-none"
                      onClick={() =>
                        setSelectedCategories(selectedCategories.filter((cat) => cat.value !== category.value))
                      }
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : selected === DATE ? (
          <div className="tw:px-0 tw:h-[370px] tw:text-center tw:sm:px-3 tw:sm:text-start">
            <DateRange
              ranges={ranges}
              showMonthAndYearPickers={false}
              showDateDisplay={false}
              months={mql.matches ? 1 : 2}
              direction="horizontal"
              rangeColors={["rgba(255, 255, 255, 0.4)"]}
              onChange={onDateRangeChange}
            />
          </div>
        ) : null}
        <div
          className={cls(`tw:absolute tw:right-2 tw:bottom-5 tw:sm:right-5`, {
            "tw:block": selected && selected !== SEARCH,
            "tw:hidden": !selected || selected === SEARCH,
          })}
        >
          <button className="tw:bg-white btn-tw-primary tw:mr-2 tw:sm:mr-5" onClick={reset}>
            Reset filters
          </button>
          <button className="tw:bg-tw-primary btn-tw-primary tw:text-white" onClick={confirm}>
            ok
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterSearchBar;
